/**
 * Affiliate Partners — admin: CRUD + API key rotation + stats.
 * Backend: feat/fee-credit-system (PR1, merged).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import './VouchersPage.css';                     // reuse table + modal styles

interface Partner {
  id: string;
  name: string;
  slug: string;
  codePrefix: string;
  email: string;
  status: 'active' | 'paused' | 'terminated';
  hasApiKey: boolean;
  apiKeyLastRotatedAt: string | null;
  webhookUrl: string | null;
  notes: string | null;
  createdAt: string;
}

interface PartnerStats {
  partnerId: string;
  vouchersIssued: number;
  redemptions: number;
  uniqueRedeemers: number;
  totalFeeCreditGranted: number;
}

const AffiliatePartnersPage: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [revealedKey, setRevealedKey] = useState<{ partnerName: string; apiKey: string } | null>(null);
  const [statsTarget, setStatsTarget] = useState<{ partner: Partner; stats: PartnerStats | null } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.listAffiliatePartners({ limit: 200, offset: 0 });
      setPartners(result.partners);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const rotateKey = async (partner: Partner) => {
    if (!window.confirm(`Rotate API key for ${partner.name}? The previous key will stop working immediately.`)) return;
    try {
      const result = await apiService.rotatePartnerApiKey(partner.id);
      setRevealedKey({ partnerName: partner.name, apiKey: result.apiKey });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Key rotation failed');
    }
  };

  const setPartnerStatus = async (partner: Partner, action: 'pause' | 'activate' | 'terminate') => {
    if (action === 'terminate' && !window.confirm(`Terminate ${partner.name}? API key will be wiped and the partner can't issue new vouchers.`)) return;
    try {
      if (action === 'pause') await apiService.pauseAffiliatePartner(partner.id);
      if (action === 'activate') await apiService.activateAffiliatePartner(partner.id);
      if (action === 'terminate') await apiService.terminateAffiliatePartner(partner.id);
      setSuccess(`Partner ${partner.name} ${action}d`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : `${action} failed`);
    }
  };

  const openStats = async (partner: Partner) => {
    setStatsTarget({ partner, stats: null });
    try {
      const stats = await apiService.getAffiliatePartnerStats(partner.id);
      setStatsTarget({ partner, stats });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Stats load failed');
      setStatsTarget(null);
    }
  };

  return (
    <div className="vouchers-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Affiliate Partners</h1>
          <div className="page-sub">{partners.length} total · partners issue voucher codes via the partner API or admin generates on their behalf</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Create partner</button>
      </div>

      {error && <div className="alert alert-error" onClick={() => setError(null)}>{error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess(null)}>{success}</div>}

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="loading">Loading…</div>
          ) : partners.length === 0 ? (
            <div className="empty">No affiliate partners yet — create one to start issuing partner-prefixed voucher codes.</div>
          ) : (
            <table className="vouchers-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Prefix</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>API key</th>
                  <th>Last rotated</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td><code>{p.slug}</code></td>
                    <td><code>{p.codePrefix}</code></td>
                    <td>{p.email}</td>
                    <td><span className={`status status-${p.status}`}>{p.status}</span></td>
                    <td>{p.hasApiKey ? 'set' : '—'}</td>
                    <td>{p.apiKeyLastRotatedAt ? new Date(p.apiKeyLastRotatedAt).toLocaleDateString() : '—'}</td>
                    <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                    <td className="row-actions">
                      <button className="btn btn-small btn-secondary" onClick={() => openStats(p)}>Stats</button>
                      {p.status !== 'terminated' && (
                        <button className="btn btn-small btn-secondary" onClick={() => rotateKey(p)}>
                          {p.hasApiKey ? 'Rotate key' : 'Issue key'}
                        </button>
                      )}
                      {p.status === 'active' && (
                        <button className="btn btn-small btn-secondary" onClick={() => setPartnerStatus(p, 'pause')}>Pause</button>
                      )}
                      {p.status === 'paused' && (
                        <button className="btn btn-small btn-secondary" onClick={() => setPartnerStatus(p, 'activate')}>Activate</button>
                      )}
                      {p.status !== 'terminated' && (
                        <button className="btn btn-small btn-danger" onClick={() => setPartnerStatus(p, 'terminate')}>Terminate</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreate && (
        <CreatePartnerModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            setSuccess('Partner created');
            void load();
          }}
        />
      )}

      {revealedKey && (
        <div className="modal-backdrop" onClick={() => setRevealedKey(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>API key for {revealedKey.partnerName}</h2>
            <p className="modal-sub">
              <strong>Copy now — this is the only time we'll show it.</strong>
              Send it to the partner via a secure channel. The hash is stored server-side; the plaintext is never displayed again.
            </p>
            <input className="api-key-output" readOnly value={revealedKey.apiKey} />
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => navigator.clipboard.writeText(revealedKey.apiKey)}>Copy</button>
              <button className="btn btn-primary" onClick={() => setRevealedKey(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {statsTarget && (
        <div className="modal-backdrop" onClick={() => setStatsTarget(null)}>
          <div className="modal modal-confirm" onClick={(e) => e.stopPropagation()}>
            <h2>Stats — {statsTarget.partner.name}</h2>
            {!statsTarget.stats ? (
              <div className="loading">Loading…</div>
            ) : (
              <dl className="stats-list">
                <div><dt>Vouchers issued</dt><dd>{statsTarget.stats.vouchersIssued}</dd></div>
                <div><dt>Redemptions</dt><dd>{statsTarget.stats.redemptions}</dd></div>
                <div><dt>Unique redeemers</dt><dd>{statsTarget.stats.uniqueRedeemers}</dd></div>
                <div><dt>Total fee credit granted</dt><dd>{statsTarget.stats.totalFeeCreditGranted.toFixed(2)} EUR</dd></div>
              </dl>
            )}
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={() => setStatsTarget(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CreatePartnerModal: React.FC<{ onClose: () => void; onCreated: () => void }> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [codePrefix, setCodePrefix] = useState('');
  const [email, setEmail] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    setSubmitting(true);
    try {
      await apiService.createAffiliatePartner({
        name,
        slug: slug.toLowerCase(),
        codePrefix: codePrefix.toUpperCase(),
        email,
        webhookUrl: webhookUrl || undefined,
        notes: notes || undefined,
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Create affiliate partner</h2>
        {err && <div className="alert alert-error">{err}</div>}
        <div className="form-grid">
          <label>
            <span>Name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Kai Creator" />
          </label>
          <label>
            <span>Slug (url-safe)</span>
            <input type="text" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="kai-creator" />
          </label>
          <label>
            <span>Code prefix (2-4 uppercase letters; "VRS" reserved)</span>
            <input type="text" value={codePrefix} onChange={(e) => setCodePrefix(e.target.value.toUpperCase().slice(0, 4))} placeholder="KAI" maxLength={4} />
          </label>
          <label>
            <span>Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kai@example.com" />
          </label>
          <label className="checkbox-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <span>Webhook URL (optional)</span>
            <input type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://partner.example.com/versus-webhook" />
          </label>
          <label className="checkbox-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <span>Notes (internal)</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="reason-input" />
          </label>
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={submitting || !name || !slug || !codePrefix || !email}>
            {submitting ? 'Creating…' : 'Create partner'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AffiliatePartnersPage;
